import React, { useState, useEffect, useRef } from 'react';
import { getAccessToken, signOut, goToPlatformLogin } from '../../lib/platformAuth';
import { useNavigate, Link } from 'react-router-dom';
import { 
  User, Bell, LogOut, Menu, Settings, 
  ShieldCheck, X, Check, Clock, Calendar, CheckCircle2 
} from 'lucide-react';
import api from '@/api/axios';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import ko from 'dayjs/locale/ko';
import { cn } from '@/lib/utils';
import {
  PASSWORD_MIN_LENGTH,
  PASSWORD_RULE_TEXT,
  isPasswordValid as isPasswordRuleOk,
} from '@/lib/passwordPolicy';
import * as signalR from '@microsoft/signalr';
import ThemeSwitcher from './ThemeSwitcher';
import FileImage from '@/components/common/FileImage';
import ProductLogo from '@/assets/images/Logo.png';
import {
  shouldShowOnDesktop, showDesktopNotification, splitNotificationMessage,
} from '@/lib/desktopNotification';

dayjs.extend(relativeTime);
dayjs.locale(ko);

/**
 * SignalR 로그.
 *
 * 예전에는 LogLevel.None 이라 연결이 아예 안 붙어도 콘솔에 흔적이 없어 원인을 찾기 어려웠다.
 * 그렇다고 전부 켜면 개발 모드(StrictMode)가 효과를 두 번 돌리며 첫 연결을 취소하는 과정에서
 * "Failed to start the HttpConnection before stop() was called." 가 매번 빨간 오류로 찍힌다.
 * 정상 동작인데 오류처럼 보이므로 그 소리만 걸러 내고, 나머지는 그대로 보여 준다.
 */
const EXPECTED_ABORT_MESSAGES = [
  'stop() was called',   // 정리 과정에서 연결 시도를 취소한 경우
  'The connection was stopped during negotiation',
];

const signalRLogger = {
  log(logLevel, message) {
    if (logLevel < signalR.LogLevel.Warning) return;

    const text = typeof message === 'string' ? message : String(message);
    if (EXPECTED_ABORT_MESSAGES.some((expected) => text.includes(expected))) return;

    if (logLevel >= signalR.LogLevel.Error) console.error('[SignalR]', text);
    else console.warn('[SignalR]', text);
  },
};

const Navbar = ({ toggleSidebar }) => {
  const apiBaseUrl = import.meta.env.VITE_API_URL || '';
  const navigate = useNavigate();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const userMenuRef = useRef(null);
  const notiRef = useRef(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  // 방금 알림이 도착했는가. 종 아이콘을 잠깐 흔들어 눈에 띄게 하는 데만 쓴다.
  const [justArrived, setJustArrived] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });
  const [currentPasswordError, setCurrentPasswordError] = useState('');
  const [userName, setUserName] = useState('');
  const [userDept, setUserDept] = useState('');

  /*
   * 회사 규칙: 숫자와 문자를 섞어 8자 이상. 서버(PasswordPolicy)와 같은 잣대다.
   * 예전에는 '6자 이상 + 특수문자' 였는데 서버는 그렇게 보지 않아 서로 어긋났다.
   */
  const hasMinLength = passwords.new.length >= PASSWORD_MIN_LENGTH;
  const hasDigitAndLetter = /\d/.test(passwords.new) && /[^\d\s]/.test(passwords.new);
  const isPasswordValid = isPasswordRuleOk(passwords.new);
  const [userPosition, setUserPosition] = useState('');
  const [profileFileId, setProfileFileId] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [companyName, setCompanyName] = useState('');

  /*
   * 무료 사용 기간.
   *
   * 로그인할 때 받아 localStorage 에 담아 둔 사용자 정보 안에 들어 있다
   * (`access` — 플랫폼이 내려 준 값을 CRM 서버가 그대로 전달한다).
   * 값이 없으면(유료이거나 옛 서버) **아무것도 그리지 않는다** — 모르는
   * 것을 「무료 아님」으로 단정하지 않는다.
   */
  const trialDaysLeft = (() => {
    try {
      const raw = localStorage.getItem('user');
      if (!raw) return null;
      const days = JSON.parse(raw)?.access?.trialDaysLeft;
      return typeof days === 'number' ? days : null;
    } catch {
      // 저장된 값이 깨졌다고 머리말 전체가 안 그려지면 안 된다.
      return null;
    }
  })();

  const trialLabel =
    trialDaysLeft === null
      ? null
      : trialDaysLeft === 0
        ? '무료 사용 · 오늘 종료'
        : `무료 사용 · ${trialDaysLeft}일 남음`;

  const trialToneClass =
    trialDaysLeft === null
      ? ''
      : trialDaysLeft <= 3
        ? 'bg-rose-100 text-rose-700'
        : trialDaysLeft <= 7
          ? 'bg-amber-100 text-amber-700'
          : 'bg-sky-100 text-sky-700';

  const trialTitle =
    trialDaysLeft === null
      ? ''
      : '기간이 끝나면 조회는 계속 가능하지만 새로 등록하거나 수정할 수 없습니다. '
        + 'WORK&Hub 의 구독 → 사용량·청구 → 결제 수단에서 결제 수단을 등록해 주세요.';


  const connectionRef = useRef(null);

  useEffect(() => {
    const handleOpenPasswordModal = () => setIsPasswordModalOpen(true);
    window.addEventListener('openPasswordModal', handleOpenPasswordModal);
    
    // 로고 업데이트 이벤트 수신
    const handleLogoUpdate = () => fetchNavbarAssets();
    window.addEventListener('logoUpdated', handleLogoUpdate);

    // 외부 클릭 감지 로직
    const handleClickOutside = (e) => {
      if (isUserMenuOpen && userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setIsUserMenuOpen(false);
      }
      if (isNotificationsOpen && notiRef.current && !notiRef.current.contains(e.target)) {
        setIsNotificationsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);

    // 브라우저 알림을 클릭했을 때 — 라우터로 옮겨야 전체 새로고침 없이 이동한다.
    const handleDesktopNotificationClick = (event) => {
      const targetUrl = event.detail?.targetUrl;
      if (targetUrl) navigate(targetUrl);
    };
    window.addEventListener('desktopNotificationClick', handleDesktopNotificationClick);

    return () => {
      window.removeEventListener('openPasswordModal', handleOpenPasswordModal);
      window.removeEventListener('logoUpdated', handleLogoUpdate);
      window.removeEventListener('desktopNotificationClick', handleDesktopNotificationClick);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen, isNotificationsOpen, navigate]);

  const fetchNavbarAssets = async () => {
    try {
      const userStr = localStorage.getItem('user');
      const userData = userStr ? JSON.parse(userStr) : null;
      
      if (!userData) {
        document.title = `SMEs CRM SYSTEM`;
        return;
      }

      const userId = userData.id || userData.Id;

      /*
       * 1. 회사 이름과 로고.
       *
       * 회사 코드를 들고 다니지 않는다. 그 값은 줄곧 비어 있어서 아래 전체가
       * 통째로 건너뛰어졌고, 제목에도 로고에도 회사가 뜨지 않았다.
       * 회사는 기업당 하나이므로 서버가 토큰을 보고 고른다.
       */
      try {
        const compRes = await api.get('/companies/current');
        const companyName = compRes.data?.companyName || compRes.data?.CompanyName;
        setCompanyName(companyName || '');
        document.title = companyName ? `WORK&CRM | ${companyName}` : `WORK&CRM`;

        /*
         * 로고는 blob 으로 받는다. <img src="/api/..."> 는 토큰이 실리지
         * 않아 기업 자료를 받아 오지 못한다(프로필 사진과 같은 이유).
         *
         * logoPath 를 보고 부를지 말지 정하지 않는다. 그 칸은 CRM 에 남아
         * 있는 **옛 파일**의 경로이고, 지금 로고의 원본은 WORK&Hub 다.
         * Hub 에만 올린 기업은 logoPath 가 비어 있어, 예전 코드는 로고가
         * 있는데도 부르지 않았다. 없으면 서버가 204 로 답한다.
         */
        try {
          const imgRes = await api.get('/companies/current/logo', { responseType: 'blob' });
          const blob = imgRes.data;
          setLogoUrl((old) => {
            if (old) URL.revokeObjectURL(old);
            return blob && blob.size > 0 ? URL.createObjectURL(blob) : null;
          });
        } catch {
          setLogoUrl((old) => {
            if (old) URL.revokeObjectURL(old);
            return null;
          });
        }
      } catch (e) {
        console.error('Failed to fetch company info:', e);
        document.title = `WORK&CRM`;
      }

      // 2. 유저 프로필 사진 조회 (최신 사진 추출)
      if (userId) {
        const profileRes = await api.get(`/files/list/USER_${userId}`);
        const pFiles = Array.isArray(profileRes.data) ? profileRes.data : [];
        
        // fileId 기준 내림차순 정렬하여 가장 최근 업로드된 사진 선택
        const latestProfile = [...pFiles].sort((a, b) => b.fileId - a.fileId)[0];

        // /files/download 는 토큰이 필요해 <img src> 로는 401. FileImage 가 blob 으로 받아 그린다.
        setProfileFileId(latestProfile ? latestProfile.fileId : null);
      }
    } catch (error) {
      console.error('Failed to fetch navbar assets:', error);
    }
  };

  useEffect(() => {
    fetchNavbarAssets();
  }, []);

  /*
   * 새 알림 표시는 잠깐만 둔다.
   * 계속 흔들리면 오히려 눈에 거슬리고, 언제부터 안 본 알림인지도 흐려진다.
   * (읽지 않은 건수 배지는 그대로 남으므로 놓칠 걱정은 없다)
   */
  useEffect(() => {
    if (!justArrived) return undefined;
    const timer = setTimeout(() => setJustArrived(false), 8000);
    return () => clearTimeout(timer);
  }, [justArrived]);

  // SignalR 연결 및 알림 처리
  useEffect(() => {
    const token = getAccessToken();
    if (!token) return;

    const baseUrl = import.meta.env.VITE_API_URL || '';
    /*
     * VITE_API_URL 은 "/api" 까지 포함한다(예: http://localhost:5243/api).
     * 따라서 허브 주소는 ".../api/hubs/notifications" 가 되며,
     * 백엔드 MapHub 경로(CRM.API/Hubs/HubPaths.cs)와 nginx 의 location /api/hubs/ 도 같은 값이다.
     * 이 셋 중 하나라도 어긋나면 연결은 되는데 알림이 오지 않는다.
     */
    const hubUrl = `${baseUrl.replace(/\/$/, '')}/hubs/notifications`;
    
    /*
     * 전송 방식은 서버와 협상해서 정한다.
     *
     * 예전에는 skipNegotiation 으로 협상을 건너뛰고 WebSocket 만 쓰게 했는데,
     * 그러면 WebSocket 이 막히는 순간(IIS 기능 미설치, 중간 프록시가 업그레이드를 차단 등)
     * 대안이 없어 연결이 계속 실패하고 자동 재연결만 무한히 돈다.
     * 협상을 켜 두면 WebSocket 이 되면 그대로 쓰고, 안 되면 SSE -> 롱폴링으로 스스로 내려간다.
     */
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => token
      })
      .withAutomaticReconnect()
      .configureLogging(signalRLogger)
      .build();

    connection.on("ReceiveNotification", (notification) => {
      console.log("New SignalR notification:", notification);
      
      // PostgreSQL 및 PascalCase 대응 속성명 정규화 (실시간 연동 보장)
      const normalizedNoti = {
        notificationId: notification.notificationId || notification.NotificationId,
        userId: notification.userId || notification.UserId,
        message: notification.message || notification.Message,
        targetUrl: notification.targetUrl || notification.TargetUrl,
        isRead: notification.isRead !== undefined ? notification.isRead : (notification.IsRead || false),
        createdAt: notification.createdAt || notification.CreatedAt
      };

      setNotifications(prev => [normalizedNoti, ...prev].slice(0, 10));
      setUnreadCount(prev => prev + 1);

      // 종 아이콘을 잠시 흔들어 새 알림이 왔다는 것을 눈에 띄게 한다.
      setJustArrived(true);

      // 우측 상단 팝업 — NotificationToaster 가 이 이벤트를 받아 띄운다.
      window.dispatchEvent(new CustomEvent('showToast', {
        detail: {
          message: normalizedNoti.message,
          targetUrl: normalizedNoti.targetUrl,
          notificationId: normalizedNoti.notificationId,
          type: 'info',
        }
      }));

      /*
       * 내 결재 차례([결재요청])와 내 문서 반려([반려])는 다른 창을 보고 있어도
       * 알 수 있도록 브라우저 알림으로 한 번 더 띄운다.
       * 받을 사람은 서버에서 이미 걸러져 오므로(현재 결재자 / 기안자) 여기서는 종류만 본다.
       */
      if (shouldShowOnDesktop(normalizedNoti.message)) {
        const { title, body } = splitNotificationMessage(normalizedNoti.message);
        showDesktopNotification({
          title,
          body,
          // 같은 문서의 알림이 여러 개 쌓이지 않도록 문서별로 묶는다.
          tag: normalizedNoti.targetUrl || `noti-${normalizedNoti.notificationId}`,
          targetUrl: normalizedNoti.targetUrl,
        });
      }
    });

    /*
     * 이 효과가 아직 살아 있는가.
     *
     * 예전에는 컴포넌트 전체가 공유하는 startedRef 로 중복 연결을 막았는데,
     * 그 가드가 개발 모드(StrictMode)에서 두 번째 연결까지 막아 버렸다.
     *   마운트 → startedRef=true, 연결 시작
     *   정리   → stop() (start() 의 거부는 비동기라 아직 안 옴)
     *   마운트 → startedRef 가 여전히 true → start() 를 아예 호출하지 않음
     * 결과는 "연결이 하나도 없는" 상태인데, 로그를 꺼 둬서 콘솔에도 아무것도 남지 않았다.
     *
     * 공유 ref 대신 효과 인스턴스마다 따로 두면 이런 엇갈림이 생기지 않는다.
     */
    let disposed = false;

    const startConnection = async () => {
      if (disposed) return;
      if (connection.state !== signalR.HubConnectionState.Disconnected) return;

      try {
        await connection.start();
        // 연결되는 사이에 화면을 떠났으면 붙잡고 있지 않는다.
        if (disposed) {
          connection.stop().catch(() => {});
          return;
        }
        console.log('SignalR Connected.');
      } catch (err) {
        const isAbortError =
          err.name === 'AbortError' ||
          err.message?.includes('stop() was called') ||
          err.message?.includes('aborted');

        // 의도적인 중단(정리 과정)이 아닐 때만 알린다.
        if (!isAbortError && !disposed) {
          console.error('SignalR Connection Error: ', err);
        }
      }
    };

    startConnection();
    connectionRef.current = connection;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && connection.state === signalR.HubConnectionState.Disconnected) {
        startConnection();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const handleUpdate = () => fetchNotifications();
    window.addEventListener('notificationUpdated', handleUpdate);

    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('notificationUpdated', handleUpdate);
      // 상태를 따지지 않고 무조건 정리한다. 어차피 이 커넥션은 다시 쓰지 않는다.
      connection.stop().catch((e) => {
        if (e.name !== 'AbortError') console.error('SignalR Stop Error:', e);
      });
    };
  }, []);
  
  useEffect(() => {
    const checkUser = async () => {
      const userStr = localStorage.getItem('user');
      if (userStr && userStr !== "undefined") {
        try {
          const userData = JSON.parse(userStr);
          const name = userData.name || userData.Name || userData.userName || userData.UserName;
          setUserName(name || '사용자');

          // 로컬스토리지에만 의존하지 않고, 백엔드 프로필 API(/api/employees/me)를 동적 호출하여
          // 진짜 부서명(DeptName) 및 진짜 직책명(positionName)을 실시간 최신화하여 화면에 반영 (이름 옆 괄호 직책 노출 보장)
          try {
            const profileRes = await api.get('/employees/me');
            if (profileRes.data) {
              if (profileRes.data.deptName) setUserDept(profileRes.data.deptName);
              if (profileRes.data.positionName) setUserPosition(profileRes.data.positionName);
            } else {
              const dept = userData.departmentName || userData.DepartmentName || userData.department || userData.Department || '';
              setUserDept(dept);
              const pos = userData.positionName || userData.PositionName || '';
              setUserPosition(pos);
            }
          } catch (profileErr) {
            // API 호출 실패 시 로컬 스토리지의 기본 값으로 대체 (Fallback 보장)
            const dept = userData.departmentName || userData.DepartmentName || userData.department || userData.Department || '';
            setUserDept(dept);
            const pos = userData.positionName || userData.PositionName || '';
            setUserPosition(pos);
          }
        } catch (e) {
          setUserName('사용자');
          setUserDept('');
          setUserPosition('');
        }
      } else {
        setUserName('로그인 필요');
        setUserDept('');
        setUserPosition('');
      }
    };

    checkUser();
    window.addEventListener('storage', checkUser);
    
    let count = 0;
    const interval = setInterval(() => {
      checkUser();
      count++;
      if (count > 6) clearInterval(interval);
    }, 500);

    return () => {
      window.removeEventListener('storage', checkUser);
      clearInterval(interval);
    };
  }, []);

  const fetchNotifications = async () => {
    try {
      const [listRes, countRes] = await Promise.all([
        api.get('/notifications'),
        api.get('/notifications/unread-count')
      ]);
      
      // 대소문자 정규화 매핑 보완 (안전장치)
      const list = Array.isArray(listRes.data) ? listRes.data.map(n => ({
        notificationId: n.notificationId || n.NotificationId,
        userId: n.userId || n.UserId,
        message: n.message || n.Message,
        targetUrl: n.targetUrl || n.TargetUrl,
        isRead: n.isRead !== undefined ? n.isRead : (n.IsRead || false),
        createdAt: n.createdAt || n.CreatedAt
      })) : [];

      setNotifications(list);
      setUnreadCount(countRes.data);
    } catch (error) {
      console.error('Failed to fetch notifications');
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const handleMarkAllAsRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read');
    }
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.isRead) {
      try {
        await api.put(`/notifications/${notification.notificationId}/read`);
        fetchNotifications();
      } catch (error) {}
    }
    if (notification.targetUrl) {
      navigate(notification.targetUrl);
      setIsNotificationsOpen(false);
    }
  };

  const handleLogout = () => {
    /*
     * ⚠ goToPlatformLogin() 을 부르면 안 된다. 그것은 **로그인하러** 보내는
     *   함수다. 플랫폼 세션이 살아 있으면 곧바로 새 코드를 받아 그대로 다시
     *   로그인된 채 돌아온다 — 눌러도 아무 일이 없는 것처럼 보인다.
     *   실제로 그렇게 되어 있었다.
     *
     * 정리는 signOut() 이 맡는다. 토큰·쿠키를 지우고 플랫폼 세션까지 끝낸다.
     */
    document.title = `SMEs CRM SYSTEM`;
    signOut();
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwords.new !== passwords.confirm) {
      alert('새 비밀번호가 일치하지 않습니다.');
      return;
    }
    try {
      await api.post('/auth/change-password', {
        currentPassword: passwords.current,
        newPassword: passwords.new
      });
      alert('비밀번호가 성공적으로 변경되었습니다. 다시 로그인해 주세요.');
      setIsPasswordModalOpen(false);
      setPasswords({ current: '', new: '', confirm: '' });
      setCurrentPasswordError('');
      
      // 로그아웃 처리 및 리다이렉트
      /*
       * "다시 로그인해 주세요" 라고 해놓고 로그인하러 보내면, 플랫폼 세션이
       * 살아 있어 그대로 다시 들어온다. 실제로 나가게 하려면 signOut() 이다.
       */
      signOut();
    } catch (error) {
      if (error.response?.status === 400) {
        const errorMsg = error.response?.data?.message || error.response?.data?.Message || '';
        if (errorMsg.includes('현재 비밀번호') || errorMsg.includes('비밀번호를 확인') || errorMsg.includes('mismatch')) {
          setCurrentPasswordError('현재 비밀번호를 확인 하십시오.');
          return; // alert 창을 띄우지 않고 함수 종료!
        }
      }
      alert('비밀번호 변경 실패: ' + (error.response?.data?.message || error.response?.data?.Message || error.response?.data?.[0]?.description || '오류가 발생했습니다.'));
    }
  };

  return (
    <>
      <header className="h-16 bg-chrome border-b border-chrome-border flex items-center justify-between px-4 lg:px-6 sticky top-0 z-40">
        <div className="flex items-center gap-4">
          <button onClick={toggleSidebar} className="text-chrome-muted transition-colors hover:text-chrome-strong">
            <Menu size={24} />
          </button>
          {/*
            머리말에는 **기업 로고**를 둔다.

            여기는 "지금 보고 있는 회사" 자리다. 예전에는 제품 로고와 기업
            로고를 나란히 뒀는데, 제품 로고는 왼쪽 아래(Sidebar 하단)에 늘
            떠 있어 같은 화면에 두 번 나오는 셈이었다.

            기업 로고를 아직 올리지 않았으면 제품 로고로 대신한다. 빈자리를
            두면 머리말이 휑하고 "그림이 깨졌다" 로 읽힌다. 로고는
            WORK&Hub 가 원본이라, 거기서 올리면 이 자리도 함께 바뀐다.
          */}
          <div className="flex items-center gap-3">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={companyName || '기업 로고'}
                className="h-8 w-auto max-w-[200px] object-contain"
              />
            ) : (
              <img src={ProductLogo} alt="WORK&CRM" className="h-7 w-auto object-contain" />
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 lg:gap-4">
          {/*
            무료 사용 기간 딱지.

            체험 기간 **내내** 떠 있다. 끝나기 직전에만 알리면 고객은 어느 날
            갑자기 등록이 막히고 나서야 체험이었다는 것을 안다.

            남은 날짜는 **플랫폼이 세 준다**(access.trialDaysLeft). CRM 이
            직접 세면 표준시간대와 경계 처리가 갈려 Hub 화면·안내 메일이
            말하는 날짜와 하루씩 어긋난다 — 돈과 기한이 걸린 안내다.

            색은 급한 정도만 알린다. 문구에 날짜가 이미 들어 있어 색을 못 봐도
            읽힌다.
          */}
          {trialLabel && (
            <span
              className={`hidden whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold sm:inline-block ${trialToneClass}`}
              title={trialTitle}
            >
              {trialLabel}
            </span>
          )}

          <ThemeSwitcher />

          <div className="relative" ref={notiRef}>
            <button
              onClick={() => {
                setIsNotificationsOpen(!isNotificationsOpen);
                setIsUserMenuOpen(false);
                // 목록을 열어 봤으면 더 흔들 이유가 없다.
                setJustArrived(false);
              }}
              className={cn(
                'text-chrome-muted hover:text-chrome-strong relative p-1.5 rounded-md hover:bg-chrome-hover transition-colors',
                // 새 알림이 오면 잠시 색이 바뀌고 종이 흔들린다.
                justArrived && 'text-amber-500 hover:text-amber-600'
              )}
              aria-label={unreadCount > 0 ? `알림 ${unreadCount}건` : '알림'}
            >
              <Bell size={20} className={cn(justArrived && 'bell-ring')} />

              {/* 새 알림이 온 직후에는 배지 둘레로 파장을 한 번 퍼뜨린다 */}
              {justArrived && (
                <span className="pointer-events-none absolute top-0.5 right-0.5 h-5 w-5 animate-ping rounded-full bg-red-400/60" />
              )}

              {unreadCount > 0 && (
                <span className={cn(
                  'absolute top-1 right-1 bg-red-500 text-white text-[10px] min-w-[16px] h-4 px-1 rounded-full flex items-center justify-center font-bold border-2 border-white',
                  justArrived && 'badge-pop'
                )}>
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {isNotificationsOpen && (
              <div className="absolute right-0 mt-2 w-80 bg-surface rounded-md shadow-[var(--shadow-pop)] border border-app-border overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                <div className="p-4 border-b border-app-border flex justify-between items-center bg-surface-2">
                  <h3 className="text-sm font-bold text-app-text flex items-center gap-2">
                    알림 {unreadCount > 0 && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded text-[10px]">{unreadCount}</span>}
                  </h3>
                  <button 
                    onClick={handleMarkAllAsRead}
                    className="text-[11px] font-bold text-blue-600 hover:text-blue-700"
                  >
                    모두 읽음
                  </button>
                </div>
                
                <div className="max-h-[400px] overflow-y-auto">
                  {notifications.length > 0 ? (
                    notifications.map((n) => (
                      <div 
                        key={n.notificationId}
                        onClick={() => handleNotificationClick(n)}
                        className={cn(
                          "p-4 border-b border-app-border cursor-pointer transition-colors hover:bg-surface-hover flex gap-3",
                          !n.isRead && "bg-blue-50/30"
                        )}
                      >
                        <div className={cn(
                          "w-8 h-8 rounded-full flex items-center justify-center shrink-0",
                          n.message.includes('마감') ? "bg-orange-100 text-orange-600" : "bg-blue-100 text-blue-600"
                        )}>
                          {n.message.includes('마감') ? <Clock size={16} /> : <Bell size={16} />}
                        </div>
                        <div className="flex-1 space-y-1">
                          <p className={cn("text-xs leading-relaxed", n.isRead ? "text-app-muted" : "text-app-text font-bold")}>
                            {n.message}
                          </p>
                          <p className="text-[10px] text-app-faint font-medium">
                            {dayjs(n.createdAt).fromNow()}
                          </p>
                        </div>
                        {!n.isRead && <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-1.5 shrink-0" />}
                      </div>
                    ))
                  ) : (
                    <div className="py-12 text-center text-app-faint">
                      <div className="flex flex-col items-center gap-2">
                        <CheckCircle2 size={32} className="opacity-20" />
                        <p className="text-sm font-medium italic">새로운 알림이 없습니다.</p>
                      </div>
                    </div>
                  )}
                </div>
                
                <Link 
                  to="/notifications" 
                  className="block p-3 text-center text-[11px] font-bold text-app-muted hover:text-brand bg-surface-2 border-t border-app-border"
                  onClick={() => setIsNotificationsOpen(false)}
                >
                  모든 알림 보기
                </Link>
              </div>
            )}
          </div>

          <div className="relative" ref={userMenuRef}>
            <button 
              onClick={() => {
                setIsUserMenuOpen(!isUserMenuOpen);
                setIsNotificationsOpen(false);
              }}
              className="flex items-center gap-2 hover:bg-chrome-hover p-1.5 rounded-md transition-colors"
            >
              <FileImage
                fileId={profileFileId}
                alt="Profile"
                className="w-8 h-8 rounded-full object-cover border border-chrome-border"
                fallback={(
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 border border-blue-200">
                    <User size={18} />
                  </div>
                )}
              />
              <div className="flex flex-col items-start leading-tight hidden md:flex">
                {/* 2단계: 안전한 조건부 렌더링 적용 */}
                <span className="text-sm font-bold text-chrome-strong">
                  {userName ? `${userName}${userPosition ? ` (${userPosition})` : ''}님` : '로그인 필요'}
                </span>
                {userDept && <span className="text-[10px] font-medium text-blue-600">({userDept})</span>}
              </div>
            </button>

            {isUserMenuOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-surface rounded-md shadow-[var(--shadow-pop)] border border-app-border py-2 z-50">
                <button 
                  onClick={() => { setIsPasswordModalOpen(true); setIsUserMenuOpen(false); }}
                  className="flex items-center gap-3 px-4 py-2 text-sm text-app-text hover:bg-brand-soft hover:text-brand w-full text-left"
                >
                  <ShieldCheck size={16} /> 비밀번호 변경
                </button>
                <button
                  onClick={() => { navigate('/mypage'); setIsUserMenuOpen(false); }}
                  className="flex items-center gap-3 px-4 py-2 text-sm text-app-text hover:bg-brand-soft hover:text-brand w-full text-left"
                >
                  <Settings size={16} /> My Page
                </button>
                <hr className="my-2 border-app-border" />
                <button 
                  onClick={handleLogout}
                  className="flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full text-left"
                >
                  <LogOut size={16} /> 로그아웃
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {isPasswordModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-surface rounded-md shadow-[var(--shadow-pop)] w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-6 border-b border-app-border flex justify-between items-center bg-surface-2">
              <h3 className="text-lg font-bold text-app-text">비밀번호 변경</h3>
              <button 
                onClick={() => {
                  setIsPasswordModalOpen(false);
                  setPasswords({ current: '', new: '', confirm: '' });
                  setCurrentPasswordError('');
                }} 
                className="text-app-faint hover:text-app-text"
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handlePasswordChange} className="p-6 space-y-4">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-app-muted uppercase tracking-wider">현재 비밀번호</label>
                <input 
                  type="password" 
                  required
                  className={cn(
                    "w-full px-4 py-2.5 bg-surface-2 text-app-text border rounded-md focus:ring-2 focus:ring-[var(--brand)]/20 outline-none transition-all",
                    currentPasswordError ? "border-red-400 focus:border-red-500" : "border-app-border focus:border-[var(--brand)]"
                  )}
                  value={passwords.current}
                  onChange={e => {
                    setPasswords({...passwords, current: e.target.value});
                    setCurrentPasswordError('');
                  }}
                />
                {currentPasswordError && (
                  <p className="text-xs text-red-500 font-bold mt-1">{currentPasswordError}</p>
                )}
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-app-muted uppercase tracking-wider">새 비밀번호</label>
                <input 
                  type="password" 
                  required
                  className="w-full px-4 py-2.5 bg-surface-2 text-app-text border border-app-border rounded-md focus:ring-2 focus:ring-[var(--brand)]/20 focus:border-blue-500 outline-none transition-all"
                  value={passwords.new}
                  onChange={e => setPasswords({...passwords, new: e.target.value})}
                />
                <p className="text-[11px] text-app-faint mt-1">{PASSWORD_RULE_TEXT}</p>
                {passwords.new.length > 0 && (
                  <div className="flex flex-col gap-1 mt-2 text-xs font-bold">
                    <span className={cn(hasMinLength ? "text-green-500" : "text-app-faint", "flex items-center gap-1 transition-colors")}>
                      <Check size={14} /> 8자 이상
                    </span>
                    <span className={cn(hasDigitAndLetter ? "text-green-500" : "text-app-faint", "flex items-center gap-1 transition-colors")}>
                      <Check size={14} /> 숫자와 문자를 함께 (특수문자 가능)
                    </span>
                  </div>
                )}
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-app-muted uppercase tracking-wider">새 비밀번호 확인</label>
                <input 
                  type="password" 
                  required
                  className={cn(
                    "w-full px-4 py-2.5 bg-surface-2 text-app-text border rounded-md focus:ring-2 focus:ring-[var(--brand)]/20 outline-none transition-all",
                    passwords.confirm && passwords.new !== passwords.confirm ? "border-red-400 focus:border-red-500" : "border-app-border focus:border-[var(--brand)]"
                  )}
                  value={passwords.confirm}
                  onChange={e => setPasswords({...passwords, confirm: e.target.value})}
                />
                {passwords.confirm && passwords.new !== passwords.confirm && (
                  <p className="text-xs text-red-500 font-bold mt-1">새 비밀번호가 일치하지 않습니다.</p>
                )}
              </div>
              <div className="pt-4 flex gap-3">
                <button 
                  type="button"
                  onClick={() => {
                    setIsPasswordModalOpen(false);
                    setPasswords({ current: '', new: '', confirm: '' });
                    setCurrentPasswordError('');
                  }}
                  className="flex-1 px-4 py-2.5 border border-app-border text-app-muted font-semibold rounded-md hover:bg-surface-hover transition-colors"
                >
                  취소
                </button>
                <button 
                  type="submit"
                  disabled={!passwords.current || !passwords.new || !passwords.confirm || passwords.new !== passwords.confirm || !isPasswordValid}
                  className="flex-1 px-4 py-2.5 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 shadow-lg shadow-blue-600/20 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  변경하기
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
